import React, { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Search, Plus, Pencil, Trash2, Upload, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import SantriPhoto from "@/components/SantriPhoto";

const STATUS_LABEL = { aktif: "Aktif", nonaktif: "Nonaktif", lulus: "Lulus" };
const STATUS_BADGE = {
  aktif: "bg-emerald-100 text-emerald-800",
  nonaktif: "bg-slate-100 text-slate-700",
  lulus: "bg-violet-100 text-violet-800",
};

function emptyForm() {
  return {
    nis: "", nama: "", tempat_lahir: "", tanggal_lahir: "",
    alamat: "", nama_wali: "", hp_wali: "", kelas_id: "",
    status: "aktif", foto_path: "", jenis_kelamin: "L",
  };
}

export default function SantriPage() {
  const { user } = useAuth();
  const canEdit = user.role === "admin" || user.role === "sekretaris";

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [kelasFilter, setKelasFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const [kelasList, setKelasList] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const loadKelas = useCallback(async () => {
    try {
      const { data } = await api.get("/kelas", { params: { limit: 100 } });
      setKelasList(data.items || []);
    } catch (_e) { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (q) params.q = q;
      if (statusFilter !== "all") params.status = statusFilter;
      if (kelasFilter !== "all") params.kelas_id = kelasFilter;
      const { data } = await api.get("/santri", { params });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [page, limit, q, statusFilter, kelasFilter]);

  useEffect(() => { loadKelas(); }, [loadKelas]);
  useEffect(() => { load(); }, [load]);

  const kelasName = useMemo(() => {
    const m = {};
    kelasList.forEach(k => { m[k.id] = k.nama; });
    return m;
  }, [kelasList]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      nis: it.nis || "", nama: it.nama || "",
      tempat_lahir: it.tempat_lahir || "", tanggal_lahir: it.tanggal_lahir || "",
      alamat: it.alamat || "", nama_wali: it.nama_wali || "", hp_wali: it.hp_wali || "",
      kelas_id: it.kelas_id || "", status: it.status || "aktif",
      foto_path: it.foto_path || "", jenis_kelamin: it.jenis_kelamin || "L",
    });
    setDialogOpen(true);
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/foto-santri", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, foto_path: data.path }));
      toast.success("Foto berhasil diunggah");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal mengunggah foto");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.kelas_id) {
      toast.error("Kelas wajib dipilih");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // empty strings to null where appropriate
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      payload.nis = form.nis;
      payload.nama = form.nama;
      payload.status = form.status;
      payload.kelas_id = form.kelas_id;
      if (editing) {
        await api.patch(`/santri/${editing.id}`, payload);
        toast.success("Data santri diperbarui");
      } else {
        await api.post("/santri", payload);
        toast.success("Santri baru ditambahkan");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/santri/${deleteTarget.id}`);
      toast.success("Santri dihapus");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="santri-page">
      <div className="flex flex-col gap-1">
        <div className="text-xs font-semibold text-emerald-700 uppercase tracking-widest">Data</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">Data Santri</h1>
        <p className="text-slate-500 text-sm">Kelola seluruh data santri Madrasah.</p>
      </div>

      <Card className="p-4 md:p-5 border border-slate-200 shadow-sm bg-white">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              placeholder="Cari nama, NIS, atau wali..."
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              data-testid="santri-search-input"
              className="pl-9 h-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="w-full md:w-44 h-10" data-testid="santri-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              <SelectItem value="aktif">Aktif</SelectItem>
              <SelectItem value="nonaktif">Nonaktif</SelectItem>
              <SelectItem value="lulus">Lulus</SelectItem>
            </SelectContent>
          </Select>
          <Select value={kelasFilter} onValueChange={(v) => { setKelasFilter(v); setPage(1); }}>
            <SelectTrigger className="w-full md:w-52 h-10" data-testid="santri-kelas-filter">
              <SelectValue placeholder="Kelas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Kelas</SelectItem>
              {kelasList.map((k) => (
                <SelectItem key={k.id} value={k.id}>{k.nama}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {canEdit && (
            <Button onClick={openCreate} data-testid="add-santri-button"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white h-10 gap-2">
              <Plus className="h-4 w-4" /> Tambah Santri
            </Button>
          )}
        </div>
      </Card>

      <Card className="border border-slate-200 shadow-sm bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Santri</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">NIS</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Kelas</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Wali</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</TableHead>
                {canEdit && <TableHead className="text-right text-xs uppercase tracking-wider text-slate-500 font-semibold">Aksi</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">
                  <Loader2 className="h-5 w-5 mx-auto animate-spin" />
                </TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">
                  Belum ada data santri.
                </TableCell></TableRow>
              ) : items.map((it) => (
                <TableRow key={it.id} data-testid={`santri-row-${it.id}`} className="hover:bg-emerald-50/40">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <SantriPhoto path={it.foto_path} alt={it.nama} />
                      <div>
                        <div className="font-semibold text-slate-900">{it.nama}</div>
                        <div className="text-xs text-slate-500">{it.tempat_lahir}{it.tanggal_lahir ? `, ${it.tanggal_lahir}` : ""}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-700">{it.nis}</TableCell>
                  <TableCell className="text-sm text-slate-700">{kelasName[it.kelas_id] || "-"}</TableCell>
                  <TableCell className="text-sm text-slate-700">
                    <div>{it.nama_wali || "-"}</div>
                    <div className="text-xs text-slate-500">{it.hp_wali || ""}</div>
                  </TableCell>
                  <TableCell>
                    <span className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${STATUS_BADGE[it.status] || "bg-slate-100"}`}>
                      {STATUS_LABEL[it.status] || it.status}
                    </span>
                  </TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(it)}
                              data-testid={`edit-santri-${it.id}`}>
                        <Pencil className="h-4 w-4 text-slate-600" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(it)}
                              data-testid={`delete-santri-${it.id}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-sm text-slate-600">
          <div>Total: <span className="font-semibold">{total}</span></div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                    data-testid="santri-prev-page">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span data-testid="santri-page-indicator">Hal {page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                    data-testid="santri-next-page">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Santri" : "Tambah Santri"}</DialogTitle>
            <DialogDescription>Isi data santri sesuai informasi yang tersedia.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="flex items-center gap-4">
              <SantriPhoto path={form.foto_path}
                className="h-20 w-20 rounded-2xl object-cover bg-emerald-50 border border-slate-200" />
              <div>
                <Label className="block">Foto Santri</Label>
                <label className="mt-1.5 inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  Unggah Foto
                  <input
                    type="file" accept="image/*" className="hidden"
                    onChange={(e) => handleUpload(e.target.files?.[0])}
                    data-testid="santri-photo-upload"
                    disabled={uploading}
                  />
                </label>
                <p className="text-xs text-slate-400 mt-1">JPG, PNG. Maks. 5 MB.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>NIS *</Label>
                <Input required value={form.nis} onChange={(e) => setForm(f => ({ ...f, nis: e.target.value }))}
                       data-testid="santri-form-nis" />
              </div>
              <div>
                <Label>Nama Lengkap *</Label>
                <Input required value={form.nama} onChange={(e) => setForm(f => ({ ...f, nama: e.target.value }))}
                       data-testid="santri-form-nama" />
              </div>
              <div>
                <Label>Tempat Lahir</Label>
                <Input value={form.tempat_lahir} onChange={(e) => setForm(f => ({ ...f, tempat_lahir: e.target.value }))}
                       data-testid="santri-form-tempat-lahir" />
              </div>
              <div>
                <Label>Tanggal Lahir</Label>
                <Input type="date" value={form.tanggal_lahir}
                       onChange={(e) => setForm(f => ({ ...f, tanggal_lahir: e.target.value }))}
                       data-testid="santri-form-tanggal-lahir" />
              </div>
              <div>
                <Label>Jenis Kelamin</Label>
                <Select value={form.jenis_kelamin} onValueChange={(v) => setForm(f => ({ ...f, jenis_kelamin: v }))}>
                  <SelectTrigger data-testid="santri-form-jk"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="L">Laki-laki</SelectItem>
                    <SelectItem value="P">Perempuan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Kelas *</Label>
                <Select value={form.kelas_id} onValueChange={(v) => setForm(f => ({ ...f, kelas_id: v }))}>
                  <SelectTrigger data-testid="santri-form-kelas"><SelectValue placeholder="Pilih kelas" /></SelectTrigger>
                  <SelectContent>
                    {kelasList.length === 0 ? (
                      <div className="px-2 py-3 text-xs text-slate-500">
                        Belum ada kelas. Tambahkan kelas terlebih dahulu.
                      </div>
                    ) : (
                      kelasList.map(k => <SelectItem key={k.id} value={k.id}>{k.nama}</SelectItem>)
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Nama Wali</Label>
                <Input value={form.nama_wali} onChange={(e) => setForm(f => ({ ...f, nama_wali: e.target.value }))}
                       data-testid="santri-form-wali" />
              </div>
              <div>
                <Label>No. HP Wali</Label>
                <Input value={form.hp_wali} onChange={(e) => setForm(f => ({ ...f, hp_wali: e.target.value }))}
                       data-testid="santri-form-hp-wali" />
              </div>
              <div className="md:col-span-2">
                <Label>Alamat</Label>
                <Textarea rows={2} value={form.alamat} onChange={(e) => setForm(f => ({ ...f, alamat: e.target.value }))}
                          data-testid="santri-form-alamat" />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger data-testid="santri-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aktif">Aktif</SelectItem>
                    <SelectItem value="nonaktif">Nonaktif</SelectItem>
                    <SelectItem value="lulus">Lulus</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
              <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="santri-form-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Santri?</AlertDialogTitle>
            <AlertDialogDescription>
              Data santri <span className="font-semibold">{deleteTarget?.nama}</span> akan dihapus permanen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="santri-delete-confirm"
                               className="bg-red-600 hover:bg-red-700">
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
